import React, { useState } from 'react';
import { DataGrid } from '@material-ui/data-grid';
import { makeStyles } from '@material-ui/styles';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import FileButton from './FileButton';
import FileActionsMenu from './FileActionsMenu';
import ParentDirectoryButton from './ParentDirectoryButton';
import DirectoryActionsMenu from './DirectoryActionsMenu';
import { useQuery } from 'react-query';
import { getDirectoryContent } from '../../../services/file-explorer/fileExplorerApi';
import UploaderDragAndDrop from '../uploader/UploaderDragAndDrop';
import ShareLinkDialogProvider from './dialogs/share-link-dialog/ShareLinkDialogProvider';
import CreateDirectoryDialogProvider from './dialogs/create-directory-dialog/CreateDirectoryDialogProvider';
import DeleteFileSystemNodeDialogProvider from './dialogs/delete-file-system-node-dialog/DeleteFileSystemNodeDialogProvider';
import RenameFileSystemNodeDialogProvider from './dialogs/rename-file-system-node-dialog/RenameFileSystemNodeDialogProvider';
import EditFileSystemNodeTagsDialogProvider from './dialogs/edit-file-system-node-tags-dialog/EditFileSystemNodeTagsDialogProvider';
import { usePermissions, useTranslate } from 'react-admin';

const useStyles = makeStyles({
    root: {
        '& .cmss--file-explorer--icon-column': {
            padding: '0px',
            minWidth: '54px',
        },
        '& .cmss--file-explorer--tags-column': {
            display: 'block',
        },
        '& .MuiDataGrid-cell:focus': {
            outline: 'none'
        },
        '& .MuiDataGrid-columnHeader:focus': {
            outline: 'none'
        },
        '& .MuiDataGrid-cell:focus-within': {
            outline: 'none'
        },
        '& .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none'
        },
    },
});

export default function DirectoryContent(props) {
    const { fileSystemNodePath, filterText, filterTags, setSelectedFileSystemNodePath, targetItemIndex, setTargetItemIndex } = props;
    const classes = useStyles();
    const translate = useTranslate();

    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const [sortModel, setSortModel] = useState([]);

    const { permissions } = usePermissions();

    const slashConcatenatedPath = fileSystemNodePath.join('/');
    const parentFileSystemNodePath = fileSystemNodePath.slice(0, fileSystemNodePath.length - 1);
    const columns = [
        {
            field: 'icon',
            headerName: ' ',
            width: 54,
            align: 'center',
            resizable: false,
            sortable: false,
            renderCell: (params) => {
                return <FileButton path={params.row.path.split('/').filter(subpath => subpath != '')} setSelectedFileSystemNodePath={setSelectedFileSystemNodePath} fileSystemNodeType={params.row.type}/>
            },
            renderHeader: (params) => {
                return <ParentDirectoryButton path={parentFileSystemNodePath} setSelectedFileSystemNodePath={setSelectedFileSystemNodePath}/>
            },
            cellClassName: 'cmss--file-explorer--icon-column',
            headerClassName: 'cmss--file-explorer--icon-column',
        },
        {
            field: 'name',
            headerName: translate(`cmss.resources.file-explorer.table.columns.name`),
            flex: 1
        },
        {
            field: 'tags',
            headerName: translate(`cmss.resources.file-explorer.table.columns.tags`),
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                return params.value.map((item) => {
                    if (item.length > 0) {
                        return <Box key={item} mx={0.5} style={{display: 'inline-block'}}><Chip size={'small'} label={item} /></Box>
                    }
                })
            },
            cellClassName: 'cmss--file-explorer--tags-column',
        },
        {
            field: 'actions',
            headerName: ' ',
            width: 54,
            align: 'center',
            resizable: false,
            sortable: false,
            renderCell: (params) => {
                return permissions?.has('share-files', 'edit-file-properties', 'delete-files') ? <FileActionsMenu isDirectory={params.row.type == 'directory'} fileSystemNodePath={params.row.path.split('/').filter(subpath => subpath != '')}/> : <></>
            },
            renderHeader: (params) => {
                return permissions?.has('upload-files') ? <DirectoryActionsMenu fileSystemNodePath={fileSystemNodePath} /> : <></>
            },
            cellClassName: 'cmss--file-explorer--icon-column',
            headerClassName: 'cmss--file-explorer--icon-column',
            hidden: true,
        },
    ];

    const filterTagIds = filterTags.map((tag) => (tag.id));
    const sortedField = sortModel?.[0]?.field;
    const sortedDirection = sortModel?.[0]?.sort;

    const page = Math.trunc(targetItemIndex / pageSize);
    const { isLoading, data } = useQuery(['/file-explorer/search', 'getDirectoryContent', slashConcatenatedPath, filterText, filterTagIds, page, pageSize, sortedField, sortedDirection], () => getDirectoryContent(slashConcatenatedPath, filterText, filterTagIds, page, pageSize, sortedField, sortedDirection),
    {
        onSuccess: (data) => {
            const newRowCount = data.total_count;
            setRowCount(newRowCount);
            const lastItemIndex = Math.max(newRowCount - 1, 0);
            if (targetItemIndex > lastItemIndex) {
                setTargetItemIndex(lastItemIndex);
            }
        },
    });

    let rows = [];
    for(let node of data?.file_system_nodes ?? []) {
        let tags = node.tags.map(x => x.name);
        tags.sort();
        rows.push({
            id: node.id,
            type: node.type,
            name: node.name,
            path: node.path,
            tags: tags,
        });
    }

    const handleOnSortModelChange = (newSortModel) => {
        if (JSON.stringify(sortModel) !== JSON.stringify(newSortModel)){
            setSortModel(newSortModel);
        }
    }

    const handleOnPageChange = (pagination) => {
        setTargetItemIndex(pagination.page * pageSize);
    }

    const handleOnPageSizeChange = (pagination) => {
        setPageSize(pagination.pageSize);
    }

    const dataGrid = <DataGrid 
        loading={isLoading}
        filterMode={'server'}
        paginationMode={'server'}
        sortingMode={'server'}
        rows={rows}
        columns={columns}
        page={page}
        onPageChange={ handleOnPageChange }
        pageSize={pageSize}
        onPageSizeChange={ handleOnPageSizeChange }
        rowsPerPageOptions={[10, 20, 50, 100]}
        rowCount={rowCount}
        sortModel={sortModel}
        onSortModelChange={handleOnSortModelChange}
        disableSelectionOnClick
        disableColumnFilter
        checkboxSelection={false}
        disableColumnMenu={true}
        density={'dense'}
        autoHeight
        className={classes.root}/>

    return (
        <>
            <ShareLinkDialogProvider>
                <CreateDirectoryDialogProvider>
                    <DeleteFileSystemNodeDialogProvider>
                        <RenameFileSystemNodeDialogProvider>
                            <EditFileSystemNodeTagsDialogProvider>
                                <UploaderDragAndDrop fileSystemNodePath={fileSystemNodePath}>
                                    <DataGrid 
                                        loading={isLoading}
                                        filterMode={'server'}
                                        paginationMode={'server'}
                                        sortingMode={'server'}
                                        rows={rows}
                                        columns={columns}
                                        page={page}
                                        onPageChange={ handleOnPageChange }
                                        pageSize={pageSize}
                                        onPageSizeChange={ handleOnPageSizeChange }
                                        rowsPerPageOptions={[10, 20, 50, 100]}
                                        rowCount={rowCount}
                                        sortModel={sortModel}
                                        onSortModelChange={handleOnSortModelChange}
                                        disableSelectionOnClick
                                        disableColumnFilter
                                        checkboxSelection={false}
                                        disableColumnMenu={true}
                                        density={'dense'}
                                        autoHeight
                                        className={classes.root}/>
                                </UploaderDragAndDrop>
                            </EditFileSystemNodeTagsDialogProvider>
                        </RenameFileSystemNodeDialogProvider>
                    </DeleteFileSystemNodeDialogProvider>
                </CreateDirectoryDialogProvider>
            </ShareLinkDialogProvider>
        </>
    );
}